<script>
  import { onDestroy, onMount } from 'svelte';
  import { getContext } from 'svelte';
  import { storyStore } from '../stores/storyStore';
  import { generateResponse, testAIConfig, testAnthropicConnection, ERROR_TYPES } from '../services/aiService';
  import { initializeChat as initializeChatSession, getChatHistory, addMessage as addChatMessage } from '../services/chatService';
  import { slide } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  import { AI_CONFIG } from '../../backend/config/aiConfig';
  import { writable, get } from 'svelte/store';
  import { slideAgentInstructions } from '../stores/agentInstructionsStore';
  import { logAgentActivity, getAgentActivityLogsByChatThread } from '../services/agentActivityService';
  import { authStore } from '../stores/authStore';
  
  const selectedSlideIndex = getContext('selectedSlideIndex');
  
  // Constants
  const SENDER_USER = 'user';
  const SENDER_AI = 'ai';
  const DEBUG_MODE = window.location.hostname === 'datacreative.app';
  
  const AGENT_INSTRUCTIONS = AI_CONFIG.agentInstructions.slideAssistant;

  // Initialize state variables
  let messages = [];
  let userInput = '';
  let isLoading = false;
  let chatBox;
  let currentInstructions;
  let editableInstructions;
  let showSettings = false;
  let currentChat = null;
  let isMounted = false;
  let isInitializing = false;
  let hasInitializedChat = false;
  let errorMessage = null;
  let isHistoryVisible = false;
  let showCustomInput = false;
  let currentSuggestions = [];

  // Connection status
  let connectionStatus = {
      checking: false,
      connected: false,
      lastChecked: null,
      error: null,
      errorType: null
  };

  let connectionCheckInterval = null;
  let connectionFailures = 0;

  const dispatch = createEventDispatcher();

  // Quick actions
  const QUICK_ACTIONS = [
    { id: 'improve-script', text: 'Help me improve this script' },
    { id: 'suggest-visuals', text: 'Suggest visuals for this slide' },
    { id: 'clarify-message', text: 'Help clarify the main message' }
  ];

  // Subscribe to agent instructions
  slideAgentInstructions.subscribe(value => {
    currentInstructions = value;
    editableInstructions = value;
  });

  // Settings functions
  function toggleSettings() {
    showSettings = !showSettings;
  }

  function saveInstructions() {
    slideAgentInstructions.set(editableInstructions);
    messages = []; 
    showSettings = false;
  }

  function resetInstructions() {
    slideAgentInstructions.reset();
    messages = []; 
    showSettings = false;
  }
  
  // Function to toggle chat history visibility
  function toggleChatHistory() {
    isHistoryVisible = !isHistoryVisible;
    
    // If showing history, scroll to top after a brief delay
    if (isHistoryVisible) {
      setTimeout(() => {
        if (chatBox) {
          chatBox.scrollTo({
            top: 0,
            behavior: 'smooth'
          }); // Scroll to top of chat history with smooth animation
        }
      }, 100);
    }
  }

  // Function to scroll to a specific message by ID
  function scrollToMessage(messageId) {
    setTimeout(() => {
      if (chatBox) {
        const messageElement = document.getElementById(messageId);
        if (messageElement) {
          messageElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center' 
          });
          
          // Add a brief highlight animation to the message
          messageElement.classList.add('highlight-message');
          setTimeout(() => {
            messageElement.classList.remove('highlight-message');
          }, 2000);
        }
      }
    }, 100);
  }

  // Generate a unique message ID
  function generateMessageId() {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Add a message to the chat
  function addMessage(text, sender, suggestions = []) {
    const messageText = typeof text === 'string' ? text : 'Invalid message format';
                   
    const newMessage = {
        id: generateMessageId(),
        content: messageText,
        text: messageText,
        sender,
        suggestions,
        timestamp: new Date()
    };
    
    messages = [...messages, newMessage];

    // If this is an AI message, also update currentSuggestions for display
    if (sender === SENDER_AI) {
        currentSuggestions = suggestions;
    }

    // Ensure chat scrolls to bottom
    setTimeout(scrollToBottom, 100);
  }

  // Function to test API connection
  async function checkApiConnection() {
    if (connectionStatus.checking) return;
    
    // Don't attempt connection check if we don't have authentication
    const userId = getUserId();
    if (!userId) {
        console.log('Skipping API connection check - user not authenticated yet');
        return false;
    }
    
    connectionStatus = {
        ...connectionStatus,
        checking: true
    };
    
    try {
        if (DEBUG_MODE) {
            console.log('DEBUG: Checking Anthropic API connection...');
        }
        
        const result = await testAnthropicConnection();
        
        connectionStatus = {
            checking: false,
            connected: result.success,
            lastChecked: new Date(),
            error: result.success ? null : result.error,
            errorType: result.success ? null : result.errorType
        };
        
        if (DEBUG_MODE) {
            console.log('DEBUG: Connection status updated:', connectionStatus);
        }
        
        // Only show firewall error if we've tried multiple times
        if (!result.success && result.errorType === ERROR_TYPES.FIREWALL) {
            // Only set error message if we've had multiple consecutive failures
            if (connectionFailures >= 2) {
                errorMessage = 'The AI service is unavailable because it appears to be blocked by your school network or firewall. Please contact your teacher or IT administrator for assistance.';
            } else {
                // Increment failure counter but don't show error yet
                connectionFailures++;
                console.log(`Connection failure ${connectionFailures}/2 - waiting for additional confirmation`);
            }
        } else if (result.success) {
            // Reset failure counter on success
            connectionFailures = 0;
        }
        
        return result.success;
    } catch (error) {
        console.error('Error checking API connection:', error);
        
        let errorType = ERROR_TYPES.UNKNOWN;
        
        connectionStatus = {
            checking: false,
            connected: false,
            lastChecked: new Date(),
            error: error.message,
            errorType
        };
        
        return false;
    }
  }

  // Set up periodic connection checking
  function setupConnectionChecking() {
    // Delay initial check to ensure app is fully loaded
    setTimeout(() => {
        checkApiConnection();
        
        // Set up interval for rechecking (every 2 minutes)
        connectionCheckInterval = setInterval(() => {
            checkApiConnection();
        }, 2 * 60 * 1000); // Check every 2 minutes
    }, 3000); // Wait 3 seconds before first check
  }

  // Function to initialize chat
  async function initializeChat() {
    try {
        console.log('Starting chat initialization...');
        
        // Set loading state
        isInitializing = true;

        // Get current story ID and user ID
        const storyId = getStoryId();
        const userId = getUserId();

        if (DEBUG_MODE) {
            console.log('DEBUG: Story ID:', storyId);
            console.log('DEBUG: User ID:', userId);
            console.log('DEBUG: Slide Index:', $selectedSlideIndex);
        }

        if (!storyId || !userId) {
            console.log('Missing required data for chat initialization');
            errorMessage = 'Cannot initialize chat. Missing required data - please make sure you are logged in.';
            isInitializing = false;
            return;
        }

        try {
            currentChat = await initializeChatSession({
                userId,
                storyId,
                chatType: 'slide-assistant',
                slideIndex: $selectedSlideIndex
            });
            
            if (!currentChat?._id) {
                throw new Error('Failed to initialize chat: No chat ID returned');
            }
            
            console.log('Chat session initialized:', currentChat);

            // Log chat initiation
            await logAgentActivity({
                chatThreadId: currentChat._id,
                userId,
                storyId,
                activityType: 'chat_initiated',
                details: {
                    slideIndex: $selectedSlideIndex,
                    slideContent: storyStore.getSlide($selectedSlideIndex)
                }
            });
        } catch (chatError) {
            console.error('Error initializing chat session:', chatError);
            errorMessage = 'Failed to initialize chat session. Please try again later.';
            isInitializing = false;
            return;
        }

        // Get chat history
        let history;
        try {
            history = await getChatHistory({
                userId,
                storyId,
                chatType: 'slide-assistant',
                slideIndex: $selectedSlideIndex
            });
        } catch (historyError) {
            console.error('Error getting chat history, continuing with empty history:', historyError);
            // Non-fatal error, continue with empty history
            history = [];
        }

        // Ensure history is an array
        const messageHistory = Array.isArray(history) ? history : [];
        console.log(`Retrieved ${messageHistory.length} messages from chat history`);

        // Process chat history
        if (messageHistory.length > 0) {
            // Chat history exists, populate the UI with the messages
            messages = messageHistory.map(msg => ({
                id: msg._id || generateMessageId(),
                content: msg.content,
                sender: msg.sender,
                timestamp: msg.timestamp || new Date(),
                suggestions: msg.suggestions || []
            }));
            
            // Set the current suggestions from the last AI message
            const lastAiMessage = messageHistory
                .filter(msg => msg.sender === 'ai')
                .pop();
                
            if (lastAiMessage && lastAiMessage.suggestions) {
                currentSuggestions = lastAiMessage.suggestions;
            }
            
            console.log('Chat history loaded successfully');
        } else {
            // No chat history, create welcome message
            console.log('No chat history found, creating welcome message');
            
            const welcomeContent = "Hi there! I'm here to help with your slide content and script. What would you like to work on?";
            const welcomeSuggestions = [
                "Help me improve my script",
                "Suggest visuals for this slide",
                "Clarify my main message"
            ];
            
            // Create welcome message for UI
            const welcomeMessage = {
                id: generateMessageId(),
                content: welcomeContent,
                sender: 'ai',
                timestamp: new Date(),
                suggestions: welcomeSuggestions
            };
            
            // Add to UI
            messages = [welcomeMessage];
            currentSuggestions = welcomeSuggestions;
            
            // Save to database
            if (currentChat?._id) {
                try {
                    await addChatMessage(currentChat._id, {
                        content: welcomeContent,
                        sender: 'ai',
                        timestamp: new Date(),
                        suggestions: welcomeSuggestions
                    });
                    
                    // Log AI message activity
                    await logAgentActivity({
                        chatThreadId: currentChat._id,
                        userId,
                        storyId,
                        activityType: 'message_received',
                        details: {
                            messageContent: welcomeContent,
                            messageType: 'welcome'
                        }
                    });
                    
                    console.log('Welcome message saved to database');
                } catch (error) {
                    console.error('Error saving welcome message:', error);
                }
            }

            // Wait for DOM update then attach button handlers
            setTimeout(attachButtonHandlers, 100);
        }

        // Set initialization flag
        hasInitializedChat = true;
        errorMessage = null;
        console.log('Chat initialization completed successfully');

    } catch (error) {
        console.error('Error initializing chat:', error);
        errorMessage = error.message.includes('chat') 
            ? 'Failed to initialize chat. Please try refreshing the page.' 
            : 'Error connecting to the server. Please check your connection and try again.';
        hasInitializedChat = false;
    } finally {
        isInitializing = false;
    }
  }

  // Function to send message
  async function sendMessage(userMessage = null) {
    const messageText = userMessage || userInput.trim();
    if (!messageText) return;

    // Check required data for sending a message
    const storyId = getStoryId();
    const userId = getUserId();
    
    if (!userId || !storyId) {
      addMessage('Sorry, I cannot respond right now. The application is still loading or missing required data. Please try refreshing the page.', 'ai');
      console.error('Cannot send message - missing required data:', {
        hasUser: !!userId,
        hasStoryId: !!storyId
      });
      return;
    }

    // Auto-initialize chat if needed
    if (!hasInitializedChat && !isInitializing) {
      isInitializing = true;
      try {
        await initializeChat();
      } catch (error) {
        console.error('Failed to initialize chat:', error);
        addMessage('Sorry, I encountered an error initializing the chat. Please try refreshing the page.', 'ai');
        isInitializing = false;
        return;
      }
      isInitializing = false;
    }

    // Check connection with automatic retry
    let isConnected = true;
    
    // If connection is unknown or previously failed, check again
    if (!connectionStatus.lastChecked || 
        (connectionStatus.lastChecked && !connectionStatus.connected)) {
      
      console.log('Checking connection before sending message...');
      isConnected = await checkApiConnection();
      
      // If first check fails, try once more after a short delay
      if (!isConnected) {
        console.log('First connection check failed, retrying after delay...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        isConnected = await checkApiConnection();
      }
      
      if (!isConnected) {
        addMessage('Sorry, I cannot respond right now. The AI service appears to be unavailable. Please try again in a moment.', 'ai');
        return;
      }
    }

    // Store the message before clearing input values
    const messageToSend = messageText;
    
    // Reset UI state
    showCustomInput = false;
    userInput = '';
    isLoading = true;
    
    // Add user message to UI
    addMessage(messageToSend, 'user');

    // Save user message to database if we have a chat session
    if (currentChat?._id) {
      try {
        await addChatMessage(currentChat._id, {
          content: messageToSend,
          sender: 'user',
          timestamp: new Date()
        });

        // Log user message activity
        await logAgentActivity({
          chatThreadId: currentChat._id,
          userId: userId,
          storyId: storyId,
          activityType: 'message_sent',
          details: {
            messageContent: messageToSend
          }
        });
      } catch (error) {
        console.error('Error saving user message:', error);
        if (DEBUG_MODE) {
          console.error('DEBUG: Error saving user message:', error);
        }
      }
    }

    // Add AI message with loading state
    const loadingMessageId = generateMessageId();
    messages = [...messages, { 
      id: loadingMessageId,
      text: "Thinking...",
      content: "Thinking...",
      sender: 'ai',
      status: 'loading'
    }];

    try {
      // Prepare context
      const currentSlide = storyStore.getSlide($selectedSlideIndex);
      const bigQuestion = storyStore.getBigQuestion();
      const context = {
        slideIndex: $selectedSlideIndex,
        slideContent: currentSlide,
        bigQuestion: bigQuestion,
        script: messageToSend,
        question: messageToSend,
        instructions: currentInstructions
      };

      const prompt = `
        ${currentInstructions}
        
        Context:
        - Slide Number: ${$selectedSlideIndex + 1}
        - Slide Content: ${JSON.stringify(currentSlide)}
        - Big Question: "${bigQuestion || 'No big question available'}"
        
        Current User Message: ${messageToSend}
        
        IMPORTANT: Format your response as JSON with a message and suggestions:
        {
          "message": "Your detailed analysis of the slide",
          "suggestions": [
            "Improve the script",
            "Add more visuals",
            "Clarify the main message"
          ]
        }
      `;

      if (DEBUG_MODE) {
        console.log('DEBUG: Prompt being sent to AI:', prompt);
      }

      const response = await generateResponse(prompt, context);
      
      if (DEBUG_MODE) {
        console.log('DEBUG: AI response received:', response);
        console.log('DEBUG: Response suggestions:', response.suggestions);
      }
      
      // Remove loading message
      messages = messages.filter(m => m.id !== loadingMessageId);
      
      // Process response text and suggestions
      let formattedText = response.text;
      
      // Create AI message object
      const aiMessageObj = {
        id: generateMessageId(),
        content: formattedText,
        text: formattedText,
        sender: 'ai',
        timestamp: new Date(),
        suggestions: response.suggestions || []
      };
      
      // Add to UI
      addMessage(formattedText, 'ai', response.suggestions || []);
      
      // Ensure custom input is hidden
      showCustomInput = false;
      
      // Wait for DOM update then attach button handlers
      setTimeout(attachButtonHandlers, 100);
      
      // Save AI message to database if we have a chat session
      if (currentChat?._id) {
        try {
          await addChatMessage(currentChat._id, {
            content: formattedText,
            sender: 'ai',
            timestamp: new Date(),
            suggestions: response.suggestions || []
          });

          // Log AI message activity
          await logAgentActivity({
            chatThreadId: currentChat._id,
            userId: userId,
            storyId: storyId,
            activityType: 'message_received',
            details: {
              messageContent: response.text,
              suggestions: response.suggestions || []
            }
          });
        } catch (error) {
          console.error('Error saving AI message:', error);
          if (DEBUG_MODE) {
            console.error('DEBUG: Error saving AI message:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Replace loading message with error
      messages = messages.filter(m => m.id !== loadingMessageId);
      addMessage('Sorry, I encountered an error. Please try again.', 'ai', []);
      
      if (currentChat && currentChat._id) {
        try {
          await addChatMessage(currentChat._id, {
            content: 'Sorry, I encountered an error. Please try again.',
            sender: 'ai',
            timestamp: new Date()
          });
        } catch (saveError) {
          console.error('Error saving error message:', saveError);
        }
      }
    } finally {
      isLoading = false;
    }
  }

  // Function to attach click handlers to AI buttons
  function attachButtonHandlers() {
    // Find all AI suggestion buttons
    const buttons = document.querySelectorAll('.ai-suggestion-button:not(.custom-message-button)');
    
    console.log(`Found ${buttons.length} suggestion buttons to attach handlers to`);
    
    // Attach click handlers to each button
    buttons.forEach(button => {
        // Remove existing listeners to prevent duplicates
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
        
        // Add click event listener
        newButton.addEventListener('click', () => {
            const message = newButton.getAttribute('data-message');
            if (message) {
                console.log(`Button clicked with message: ${message}`);
                sendMessage(message);
            }
        });
    });
    
    // Handle custom message button separately
    const customButton = document.querySelector('.custom-message-button');
    if (customButton) {
        const newCustomButton = customButton.cloneNode(true);
        customButton.parentNode.replaceChild(newCustomButton, customButton);
        
        newCustomButton.addEventListener('click', () => {
            showCustomInput = true;
            // Force Svelte to update
            setTimeout(() => {
                const inputElement = document.querySelector('.custom-input-container input');
                if (inputElement) {
                    inputElement.focus();
                }
            }, 100);
        });
    }
  }

  // Auto-scroll function
  function scrollToBottom() {
    if (chatBox) {
        setTimeout(() => {
            // Use smooth scrolling behavior for a nicer UX
            chatBox.scrollTo({
              top: chatBox.scrollHeight,
              behavior: 'smooth'
            });
        }, 100);
    }
  }

  // Handle quick action
  function handleQuickAction(actionId) {
    const action = QUICK_ACTIONS.find(a => a.id === actionId);
    if (action) {
      userInput = action.text;
      sendMessage(action.text);
    }
  }

  onMount(async () => {
    isMounted = true;
    
    try {
      // Test general config
      const config = await testAIConfig();
      console.log('AI Config loaded:', config);
      
      // Set up connection checking
      setupConnectionChecking();
      
      // Initialize chat
      await initializeChat();
    } catch (error) {
      console.error('Failed to load AI config:', error);
      if (DEBUG_MODE) {
        console.error('DEBUG: Failed to load AI config:', error);
      }
    }
  });

  // Cleanup on component destruction
  onDestroy(() => {
    isMounted = false;
    if (connectionCheckInterval) {
      clearInterval(connectionCheckInterval);
    }
    
    // Log chat closure if we have a chat session
    if (currentChat) {
      const storyId = getStoryId();
      const userId = getUserId();
      
      logAgentActivity({
        chatThreadId: currentChat._id,
        userId,
        storyId,
        activityType: 'chat_closed',
        details: {
          messageCount: messages.length
        }
      }).catch(error => {
        console.error('Error logging chat closure:', error);
      });
    }
  });

  function getUserId() {
    // Get the user ID from the auth store
    const auth = get(authStore);
    return auth?.user?.uid;
  }

  function getStoryId() {
    // Get the story ID from the story store
    const story = get(storyStore);
    return story?._id;
  }
</script>

<div class="agent-slide-container">
  {#if !connectionStatus.connected && connectionStatus.lastChecked && connectionFailures >= 2}
    <div class="connection-status-bar" class:firewall={connectionStatus.errorType === 'firewall'}>
        <div class="connection-icon">
            <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="connection-message">
            {#if connectionStatus.errorType === 'firewall'}
                <strong>School Firewall Detected:</strong> The AI service appears to be blocked by your school network. Please inform your teacher.
            {:else if connectionStatus.errorType === 'network'}
                <strong>Network Issue:</strong> Cannot connect to the AI service. Please check your internet connection.
            {:else if connectionStatus.errorType === 'auth'}
                <strong>Authentication Issue:</strong> Cannot authenticate with the AI service.
            {:else}
                <strong>Connection Problem:</strong> Cannot connect to AI service. {connectionStatus.error}
            {/if}
        </div>
        <button class="retry-connection" on:click={checkApiConnection}>
            {connectionStatus.checking ? 'Checking...' : 'Retry Connection'}
        </button>
    </div>
  {/if}

  <div class="messages-container" bind:this={chatBox} style="scroll-behavior: smooth;">
    <!-- History toggle button at top right -->
    <div class="history-button-container">
      <button 
        class="history-icon-button" 
        on:click={toggleChatHistory} 
        title={isHistoryVisible ? "Show recent messages" : "Show full conversation"}
      >
        {#if isHistoryVisible}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
            <path fill="currentColor" d="M19,3H5C3.89,3 3,3.89 3,5V9H5V5H19V19H5V15H3V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M10.08,15.58L11.5,17L16.5,12L11.5,7L10.08,8.41L12.67,11H3V13H12.67L10.08,15.58Z" />
          </svg>
        {:else}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
            <path fill="currentColor" d="M13.5,8H12V13L16.28,15.54L17,14.33L13.5,12.25V8M13,3A9,9 0 0,0 4,12H1L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3" />
          </svg>
        {/if}
      </button>
    </div>

    <!-- Error and loading states -->
    {#if errorMessage}
        <div class="error-message">
            {errorMessage}
            <button class="retry-button" on:click={() => { errorMessage = null; hasInitializedChat = false; initializeChat(); checkApiConnection(); }}>
                Retry
            </button>
        </div>
    {/if}
    
    <!-- Show loading state when initializing -->
    {#if isInitializing && messages.length === 0}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Initializing AI assistant...</p>
        </div>
    {:else if !getUserId() || !getStoryId()}
        <div class="loading-container">
            <div class="info-icon">
                <i class="fas fa-info-circle"></i>
            </div>
            <p>AI assistant is waiting for required data to load. Please ensure you're logged in.</p>
        </div>
    {:else if messages.length === 0 && !isInitializing}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading slide assistant...</p>
        </div>
    {:else}
        <!-- Main chat view -->
        <div class="current-exchange">
            {#if messages.length > 0}
                {#if !isHistoryVisible}
                    <!-- Show only latest exchange when history is not visible -->
                    {#if messages[messages.length - 1] && messages[messages.length - 1].sender === 'ai'}
                        <!-- Show last user message and AI response pair -->
                        {#if messages.length > 1}
                            <div class="message user">
                                <div class="message-content">
                                    {messages[messages.length - 2].content}
                                </div>
                            </div>
                        {/if}
                        <div class="message ai" class:loading={messages[messages.length - 1].text === 'Thinking...'}>
                            <div class="message-content">
                                {#if messages[messages.length - 1].status === 'loading'}
                                    <div class="loading-indicator">
                                        <span></span><span></span><span></span>
                                    </div>
                                {:else if messages[messages.length - 1].status === 'error'}
                                    <div class="error-message">
                                        Error: {messages[messages.length - 1].content}. Please try again.
                                    </div>
                                {:else}
                                    {messages[messages.length - 1].content}
                                {/if}
                                {#if messages[messages.length - 1].suggestions && messages[messages.length - 1].suggestions.length > 0}
                                    <div class="ai-suggestion-buttons">
                                        {#each messages[messages.length - 1].suggestions as suggestion}
                                            <button 
                                                class="ai-suggestion-button" 
                                                data-message={suggestion}
                                                on:click={() => sendMessage(suggestion)}
                                            >
                                                {suggestion}
                                            </button>
                                        {/each}
                                        <button 
                                            class="ai-suggestion-button custom-message-button"
                                            on:click={() => showCustomInput = true}
                                        >
                                            Type your own message...
                                        </button>
                                        {#if showCustomInput}
                                            <div class="custom-input-container" transition:slide>
                                                <input
                                                    type="text"
                                                    bind:value={userInput}
                                                    placeholder="Type your message..."
                                                    on:keydown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                                                />
                                                <button
                                                    class="send-custom-btn"
                                                    on:click={() => sendMessage()}
                                                    disabled={!userInput.trim()}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                                        <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        {/if}
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {:else if messages[messages.length - 1] && messages[messages.length - 1].sender === 'user'}
                        <!-- Just user message, waiting for AI response -->
                        <div class="message user">
                            <div class="message-content">
                                {messages[messages.length - 1].content}
                            </div>
                        </div>
                        <div class="message ai loading">
                            <div class="message-content">
                                <div class="loading-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        </div>
                    {/if}
                {:else}
                    <!-- Show full message history -->
                    {#each messages as message, i (message.id)}
                        {#if !message.isHistorical || isHistoryVisible}
                            <div 
                                id={message.id}
                                class="message {message.sender === SENDER_USER ? 'user-message' : 'ai-message'}" 
                                class:historical={message.isHistorical}
                            >
                                <div class="message-content">
                                    {#if message.sender === 'ai'}
                                        {#if message.status === 'loading'}
                                            <div class="loading-indicator">
                                                <span></span><span></span><span></span>
                                            </div>
                                        {:else if message.status === 'error'}
                                            <div class="error-message">
                                                Error: {message.content}. Please try again.
                                            </div>
                                        {:else}
                                            {message.content}
                                        {/if}
                                        {#if message.id === messages[messages.length - 1].id && message.suggestions && message.suggestions.length > 0}
                                            <div class="ai-suggestion-buttons">
                                                {#each message.suggestions as suggestion}
                                                    <button 
                                                        class="ai-suggestion-button" 
                                                        data-message={suggestion}
                                                        on:click={() => sendMessage(suggestion)}
                                                    >
                                                        {suggestion}
                                                    </button>
                                                {/each}
                                                <button 
                                                    class="ai-suggestion-button custom-message-button"
                                                    on:click={() => showCustomInput = true}
                                                >
                                                    Type your own message...
                                                </button>
                                            </div>
                                        {/if}
                                    {:else}
                                        {message.content}
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    {/each}
                    {#if showCustomInput}
                        <div class="custom-input-container" transition:slide>
                            <input
                                type="text"
                                bind:value={userInput}
                                placeholder="Type your message..."
                                on:keydown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                            />
                            <button
                                class="send-custom-btn"
                                on:click={() => sendMessage()}
                                disabled={!userInput.trim()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                    <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                                </svg>
                            </button>
                        </div>
                    {/if}
                {/if}
            {/if}
        </div>

        <!-- History button - only show when there's more than one message -->
        {#if messages.length > 1}
          <div class="history-button-container">
            <button 
              class="history-icon-button" 
              on:click={toggleChatHistory} 
              title={isHistoryVisible ? "Show recent messages" : "Show full conversation"}
            >
              {#if isHistoryVisible}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                  <path fill="currentColor" d="M19,3H5C3.89,3 3,3.89 3,5V9H5V5H19V19H5V15H3V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M10.08,15.58L11.5,17L16.5,12L11.5,7L10.08,8.41L12.67,11H3V13H12.67L10.08,15.58Z" />
                </svg>
              {:else}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                  <path fill="currentColor" d="M13.5,8H12V13L16.28,15.54L17,14.33L13.5,12.25V8M13,3A9,9 0 0,0 4,12H1L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3" />
                </svg>
              {/if}
            </button>
          </div>
        {/if}
    {/if}

    <!-- Add connection warning here -->
    {#if !connectionStatus.connected && connectionStatus.lastChecked}
      <div class="connection-warning">
        <p>Connection to AI service unavailable. Retrying...</p>
      </div>
    {/if}
  </div>
</div>

{#if showSettings}
  <div class="settings-panel">
    <div class="settings-group">
      <label for="systemInstructions">System Instructions:</label>
      <textarea id="systemInstructions" bind:value={editableInstructions} rows="5"></textarea>
    </div>
    <div class="settings-actions">
      <button on:click={saveInstructions} class="btn-save">Save</button>
      <button on:click={resetInstructions} class="btn-reset">Reset</button>
    </div>
  </div>
{/if}

<style>
  .agent-slide-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-height: 100%;
    background-color: #f8fafc;
    overflow: hidden;
  }

  /* Connection status bar */
  .connection-status-bar {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background-color: rgba(254, 242, 242, 0.9);
    color: #b91c1c;
    gap: 0.5rem;
    margin: 0;
    border-radius: 0;
    font-size: 0.85rem;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 20;
  }

  .connection-icon {
    font-size: 0.9rem;
  }

  .retry-connection {
    background-color: #ef4444;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .retry-connection:hover {
    background-color: #dc2626;
  }

  /* New minimalist chat interface */
  .messages-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    margin: 0;
    border-radius: 0;
    background: #121212;
    overflow: hidden;
    box-shadow: none;
    transition: all 0.3s ease-in-out;
  }

  .current-exchange {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 1.5rem 1rem;
    padding-top: 3rem;
    overflow-y: auto;
    max-height: 100%;
    transition: all 0.3s ease-in-out;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }

  .current-exchange::-webkit-scrollbar {
    width: 6px;
  }

  .current-exchange::-webkit-scrollbar-track {
    background: transparent;
  }

  .current-exchange::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }

  .message {
    margin-bottom: 1.5rem;
    max-width: 95%;
    animation: fadeIn 0.3s ease forwards;
  }
  
  @keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
  }

  .message.user {
    align-self: flex-end;
  }

  .message.ai {
    align-self: flex-start;
  }

  .message-content {
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: pre-wrap;
    border-radius: 12px;
    padding: 10px 14px;
    position: relative;
  }

  .message.user .message-content {
    background-color: #2563eb;
    color: white;
    border-bottom-right-radius: 4px;
  }

  .message.ai .message-content {
    background-color: #1e1e1e;
    color: rgba(255, 255, 255, 0.95);
    border-bottom-left-radius: 4px;
  }

  /* AI suggestion buttons styles */
  :global(.ai-suggestion-buttons) {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
    width: 100%;
  }

  :global(.ai-suggestion-button) {
    padding: 8px 14px;
    background-color: rgba(96, 165, 250, 0.15);
    color: #60A5FA;
    border: 1px solid rgba(96, 165, 250, 0.3);
    border-radius: 16px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    margin: 3px 0;
  }

  :global(.ai-suggestion-button:hover) {
    background-color: rgba(96, 165, 250, 0.25);
    border-color: rgba(96, 165, 250, 0.5);
    transform: translateY(-2px);
  }

  :global(.ai-suggestion-button:active) {
    transform: scale(0.98);
  }

  .message.loading .message-content {
    color: rgba(255, 255, 255, 0.5);
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% { opacity: 0.5; }
    50% { opacity: 0.8; }
    100% { opacity: 0.5; }
  }

  /* History button and chat history */
  .history-button-container {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    justify-content: flex-end;
    z-index: 10;
    pointer-events: none; /* Allow clicking through the container */
  }

  .history-icon-button {
    background: rgba(60, 60, 60, 0.5);
    border: none;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    opacity: 0.8;
    pointer-events: auto; /* Ensure the button itself is clickable */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  .history-icon-button:hover {
    background: rgba(80, 80, 80, 0.7);
    color: rgba(255, 255, 255, 1);
    opacity: 1;
    transform: translateY(-1px);
  }

  .history-toggle-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(60, 60, 60, 0.5);
    color: rgba(255, 255, 255, 0.7);
    border: none;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
    z-index: 5;
  }
  
  .history-toggle-btn:hover {
    background: rgba(80, 80, 80, 0.7);
    color: white;
  }
  
  .history-toggle-btn.active {
    background: rgba(37, 99, 235, 0.7);
    color: white;
  }
  
  /* Responsive adjustments for the history button */
  @media (max-width: 640px) {
    .history-button-container {
        top: 0.5rem;
        right: 0.5rem;
    }
    
    .history-icon-button {
        padding: 6px;
    }
    
    .history-icon-button svg {
        width: 16px;
        height: 16px;
    }
    
    .current-exchange {
        padding-top: 3rem; /* Less padding on mobile */
    }
  }

  /* Error and loading states */
  .error-message {
    color: #ef4444;
    padding: 0.75rem;
    margin: 0.5rem;
    background-color: rgba(239, 68, 68, 0.1);
    border-radius: 8px;
    text-align: center;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }
  
  .retry-button {
    margin-top: 0.5rem;
    padding: 0.375rem 0.75rem;
    background-color: #ef4444;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background-color 0.2s;
  }
  
  .retry-button:hover {
    background-color: #dc2626;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
    min-height: 150px;
  }

  .loading-container p {
    margin-top: 1rem;
    font-size: 0.875rem;
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top-color: rgba(255, 255, 255, 0.5);
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .info-icon {
    font-size: 24px;
    color: rgba(255, 255, 255, 0.5);
  }

  /* Settings panel styles */
  .settings-panel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(3px);
  }

  .settings-group {
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }

  .settings-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #334155;
  }

  #systemInstructions {
    width: 100%;
    min-height: 150px;
    padding: 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 1rem;
    resize: vertical;
  }

  #systemInstructions:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .settings-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .btn-save, .btn-reset {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-save {
    background: #2563eb;
    color: white;
    border: 1px solid transparent;
  }

  .btn-save:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
  }

  .btn-reset {
    background: transparent;
    color: #64748b;
    border: 1px solid #cbd5e1;
  }

  .btn-reset:hover {
    background: #f1f5f9;
    color: #334155;
    transform: translateY(-1px);
  }

  /* Empty state styling */
  .empty-state {
    text-align: center;
    padding: 2rem;
    color: rgba(255, 255, 255, 0.6);
  }

  .empty-state p {
    font-size: 0.95rem;
    margin-bottom: 1rem;
  }

  /* Custom input styling */
  .custom-input-container {
    display: flex;
    gap: 6px;
    width: 100%;
    margin-top: 10px;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    align-items: center;
    transition: all 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  .custom-input-container:focus-within {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .custom-input-container input {
    flex: 1;
    background: transparent;
    border: none;
    padding: 8px;
    color: #fff;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .custom-input-container input:focus {
    outline: none;
  }

  .custom-input-container input::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  .send-custom-btn {
    background: #2563eb;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    padding: 0;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .send-custom-btn:hover {
    background: #1d4ed8;
    transform: scale(1.05);
  }

  .send-custom-btn:disabled {
    background: rgba(96, 165, 250, 0.3);
    cursor: not-allowed;
    transform: none;
  }

  /* Loading indicator for messages */
  .loading-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .loading-indicator span {
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
  }

  .loading-indicator span:nth-child(1) {
    animation-delay: -0.32s;
  }

  .loading-indicator span:nth-child(2) {
    animation-delay: -0.16s;
  }

  .loading-indicator span:nth-child(3) {
    animation-delay: 0s;
  }

  @keyframes bounce {
    0%, 80%, 100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }

  /* Responsive adjustments */
  @media (max-width: 640px) {
    .messages-container {
      margin: 0;
    }
    
    .current-exchange {
      padding: 1rem 0.5rem;
      padding-top: 3rem;
    }
    
    .message {
      max-width: 98%;
    }
    
    :global(.ai-suggestion-button) {
      padding: 6px 10px; /* Even smaller on mobile */
      font-size: 0.8rem;
      border-radius: 14px;
    }
    
    .custom-input-container {
      padding: 6px 10px;
    }
    
    .message-content {
      padding: 8px 12px; /* Further reduce message padding on mobile */
      font-size: 0.9rem;
    }
  }

  /* Make the custom message button more prominent */
  .custom-message-button {
    background-color: rgba(96, 165, 250, 0.07) !important;
    border-style: dashed !important;
    color: rgba(255, 255, 255, 0.8) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-top: 10px !important;
  }

  .custom-message-button:hover {
    background-color: rgba(96, 165, 250, 0.15) !important;
    color: white !important;
  }

  /* Connection warning */
  .connection-warning {
    background-color: rgba(255, 243, 205, 0.15);
    padding: 0.75rem;
    margin: 0.5rem;
    border-radius: 8px;
    text-align: center;
    color: #fbbf24;
    font-size: 0.8rem;
    border: 1px solid rgba(251, 191, 36, 0.3);
    position: absolute;
    bottom: 5px;
    left: 5px;
    right: 5px;
    z-index: 10;
  }

  /* Message highlight animation */
  @keyframes highlightPulse {
    0% { background-color: rgba(96, 165, 250, 0); }
    50% { background-color: rgba(96, 165, 250, 0.2); }
    100% { background-color: rgba(96, 165, 250, 0); }
  }

  .highlight-message {
    animation: highlightPulse 2s ease-in-out;
    border-radius: 8px;
  }
</style>